/**
 * The region vocabulary for `GET /aggregator/providers?region=` —
 * client-owned, per epic #6's Q15.
 *
 * `region` is a **free string with no enum** on the wire
 * (`ProviderCatalogResponse`'s own schema carries no allowed-values list),
 * and there is no endpoint that lists supported regions. The backend's own
 * guidance: this vocabulary is ours, the same way `accountGroups.ts` owns the
 * account-type vocabulary because the backend does not validate `type`
 * either.
 *
 * **`region` is now OPTIONAL on the wire** (`openapi.json`, refreshed
 * 2026-09-12) and resolves server-side when omitted — linked-account
 * currency, then device locale, then a default — which is `resolve_user_
 * region()`'s own order, not a guess this client makes. **`Connect.tsx`
 * sends `region` ONLY when the user has made an explicit pick**, and omits
 * the parameter entirely otherwise, displaying the region the SERVER
 * resolved (`ProviderCatalog.region`, from `useAggregator.ts`) as the
 * selector's default in the meantime. Nothing here is persisted
 * client-side and there is no `preferences` key — `users.country_code`
 * stays the backend's one durable source of truth, still derived
 * server-side, and a second client-held value would diverge from what
 * category gating actually reads.
 *
 * **No client-side default lives here.** An earlier version exported
 * `defaultRegion()` — a device-locale guess — as a seam for the day
 * `users.country_code` is worth reading client-side. Architect review
 * (#397, N5): a zero-call-site function is not a seam, it is a guess about
 * a future call site the one-line swap does not need in advance. Deleted,
 * along with the non-empty-tuple type on `REGIONS` that existed only to
 * serve it — reversed by the same person who asked for it (team-lead,
 * #397), on the architect's argument rather than in spite of it.
 */

export type RegionOption = { value: string; label: string }

/**
 * A short, explicit list rather than every ISO 3166-1 country — this app
 * ships in Canada and the US today (master PRD §4), and an unbounded list
 * would let a user pick a region no provider actually serves, which
 * `GET /providers` would then answer with an empty catalogue for no reason a
 * user could guess. Extending this list is a one-line, deliberate change,
 * matching `MANUAL_ACCOUNT_TYPES`'s own reasoning for staying short and named.
 */
export const REGIONS: readonly RegionOption[] = [
  { value: 'CA', label: 'Canada' },
  { value: 'US', label: 'United States' },
]
